// Donate.js - Complete Version with Date Slot Availability
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Donate.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService, { getReceiptUrl, BASE_URL } from '../services/api';

// --- Main Donate Component ---
export default function Donate() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const donationTypeFromUrl = queryParams.get('type');

  // Map URL params to frontend types
  const typeMapping = {
    'sponsor-student': 'sponsor_student',
    'sponsorship-donation': 'sponsorship',
    'food-donation': 'food'
  };

  const initialDonationType = typeMapping[donationTypeFromUrl] || '';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    pan_card: '',
    amount: '',
    donation_type: initialDonationType,
    purpose_id: '',
    food_item_id: '',
    meal_date: '',
    will_serve: '0',
    serving_members: '',
    arrival_time: '',
    special_notes: '',
    message: '',
    paymentMethod: 'online'
  });
  
  const [formStatus, setFormStatus] = useState({ 
    submitted: false, 
    success: false, 
    message: '',
    receiptUrl: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showFullForm, setShowFullForm] = useState(false);
  const [donorData, setDonorData] = useState(null);
  const [isExistingDonor, setIsExistingDonor] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [purposes, setPurposes] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [showServingFields, setShowServingFields] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);

  // Booking availability state
  const [bookedSlots, setBookedSlots] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isDateAvailable, setIsDateAvailable] = useState(true);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [selectedMealName, setSelectedMealName] = useState('');
  const [toasts, setToasts] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [errors, setErrors] = useState({});
    // ===== HOLIDAY STATE =====
  const [holidayData, setHolidayData] = useState(null);
  const [isHoliday, setIsHoliday] = useState(false);
  const [checkingHoliday, setCheckingHoliday] = useState(false);
  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          setRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          setRazorpayLoaded(false);
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  // Fetch purpose master, food master, and website info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purposeResult, foodResult, websiteResult] = await Promise.all([
          apiService.fetchPurposeMaster(),
          apiService.fetchFoodMaster(),
          apiService.fetchWebinfo()
        ]);

        console.log('Website Result:', websiteResult);

        if (purposeResult.status === 'success') {
          setPurposes(purposeResult.data || []);
        }

        if (foodResult.status === 'success') {
          setFoodItems(foodResult.data || []);
          console.log('🍽️ Food Items loaded:', foodResult.data);
        }

        if (websiteResult.status === 'success' && websiteResult.data && websiteResult.data.length > 0) {
          setWebsiteInfo(websiteResult.data[0]);
        } else {
          setWebsiteInfo(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Check donor when phone changes
  useEffect(() => {
    const checkDonor = async () => {
      if (isPhoneValid && formData.phone.length >= 10) {
        try {
          const result = await apiService.checkDonorByPhone(formData.phone);
          
          if (result.success && result.data && result.data.exists) {
            const donor = result.data.donor;
            setDonorData(donor);
            setIsExistingDonor(true);
            setFormData(prev => ({
              ...prev,
              first_name: donor.first_name || '',
              last_name: donor.last_name || '',
              email: donor.email || '',
              address: donor.address || '',
              pan_card: donor.pan_card || ''
            }));
            setPaymentError('');
          } else {
            setDonorData(null);
            setIsExistingDonor(false);
          }
        } catch (error) {
          console.error('Error checking donor:', error);
        }
      }
    };

    const timeoutId = setTimeout(checkDonor, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.phone, isPhoneValid]);
  // ============================================
  // CHECK HOLIDAY FOR DATE
  // ============================================
  const checkHolidayForDate = async (date) => {
    if (!date) {
      setIsHoliday(false);
      setHolidayData(null);
      return { is_holiday: false };
    }

    setCheckingHoliday(true);
    try {
      const result = await apiService.checkHoliday(date);
      console.log('🙏 Holiday check result:', result);

      if (result.status && result.is_holiday) {
        setIsHoliday(true);
        setHolidayData(result);
        return result;
      } else {
        setIsHoliday(false);
        setHolidayData(null);
        return { is_holiday: false };
      }
    } catch (err) {
      console.error('Holiday check error:', err);
      setIsHoliday(false);
      setHolidayData(null);
      return { is_holiday: false };
    } finally {
      setCheckingHoliday(false);
    }
  };
  // ============================================
  // LOAD BOOKED SLOTS - FOR FOOD DONATIONS
  // ============================================
  const loadBookedSlots = async (foodId) => {
    if (!foodId) {
      setBookedSlots({});
      return;
    }
    
    setCheckingAvailability(true);
    try {
      // ✅ Use fetchFoodSponsorships from apiService
      const response = await apiService.fetchFoodSponsorships();
      const slots = {};
      
      console.log('Food Sponsorships Response:', response);
      
      // ✅ Check the response structure properly
      let bookings = [];
      if (response && response.status === 'success' && response.data) {
        bookings = Array.isArray(response.data) ? response.data : [response.data];
      }
      
      console.log('All bookings:', bookings);
      
      // ✅ Filter only Confirmed status (case sensitive)
      const confirmed = bookings.filter(b => {
        const status = (b.status || '').trim();
        return status === 'Confirmed';
      });
      
      console.log('Confirmed bookings:', confirmed);
      
      confirmed.forEach(booking => {
        if (booking.meal_date) {
          const date = booking.meal_date;
          if (!slots[date]) slots[date] = [];
          slots[date].push({
            id: booking.id,
            food_id: booking.food_id,
            status: booking.status,
            meal_date: booking.meal_date,
            meal_type: booking.meal_type || '',
          });
        }
      });
      
      console.log('Booked slots by date:', slots);
      setBookedSlots(slots);
      
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots({});
    } finally {
      setCheckingAvailability(false);
    }
  };

  // ============================================
  // CHECK DATE AVAILABILITY - FIXED
  // ============================================
const checkDateAvailability = (date, mealId) => {
  if (!date || !mealId) {
    return { available: true, message: '' };
  }
  
  // ✅ Debug - log what we're checking
  console.log('Checking availability for:', { date, mealId });
  console.log('Booked slots:', bookedSlots);
  
  const slots = bookedSlots[date] || [];
  
  console.log('Slots for date:', date, slots);
  
  // Check if this specific meal is booked on this date
  const existingBooking = slots.find(booking => {
    const match = String(booking.food_id) === String(mealId);
    console.log(`Comparing food_id: ${booking.food_id} === ${mealId}? ${match}`);
    return match;
  });
  
  console.log('Existing booking found:', existingBooking);
  
  if (existingBooking) {
    // Get the meal name from foodItems
    const meal = foodItems.find(item => String(item.id) === String(mealId));
    const mealName = meal?.meal_name || selectedMealName || 'This meal';
    
    // Parse date properly
    const dateObj = new Date(date + 'T00:00:00');
    const formattedDate = dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    return { 
      available: false, 
      message: `${mealName} is already booked for ${formattedDate}. Please choose another date.`
    };
  }
  
  // Get meal name for available message
  const meal = foodItems.find(item => String(item.id) === String(mealId));
  const mealName = meal?.meal_name || selectedMealName || 'This meal';
  
  // Parse date properly
  const dateObj = new Date(date + 'T00:00:00');
  const formattedDate = dateObj.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
  
  return { 
    available: true, 
    message: `✅ Available - You can book ${mealName} for ${formattedDate}` 
  };
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'phone') {
      const isValid = value.length >= 10 && /^[0-9]{10,15}$/.test(value);
      setIsPhoneValid(isValid);
      setShowFullForm(isValid);
    }

    if (name === 'amount') {
      const numValue = parseFloat(value);
      if (value && (isNaN(numValue) || numValue < 1)) {
        setAmountError('Minimum amount is ₹1');
      } else {
        setAmountError('');
      }
    }

    if (name === 'will_serve') {
      setShowServingFields(value === '1');
    }
    if (name === 'food_item_id') {
      const selectedFood = foodItems.find(item => String(item.id) === String(value));
      console.log('Selected food from handleChange:', selectedFood);
      
      if (selectedFood) {
        const minQty = selectedFood.min_quantity || 1;
        const totalPrice = parseFloat(selectedFood.price_per_plate) * minQty;
        setFormData(prev => ({
          ...prev,
          food_item_id: value,
          amount: totalPrice.toString()
        }));
        setAmountError('');
        // Set selected meal name
        setSelectedMealName(selectedFood.meal_name || '');
        // ✅ Load booked slots for this meal
        loadBookedSlots(value);
        // Reset date availability
        setIsDateAvailable(true);
        setAvailabilityMessage('');
        setErrors(prev => ({ ...prev, meal_date: '' }));
      }
    }
    if (name === 'meal_date') {
      // Check holiday first, then availability
      if (formData.food_item_id && value) {
        (async () => {
          // 1) Holiday check
          const holiday = await checkHolidayForDate(value);

          if (holiday.status && holiday.is_holiday) {
            setIsDateAvailable(false);
            setAvailabilityMessage(holiday.message);
            setErrors(prev => ({ ...prev, meal_date: holiday.message }));
            showToast(`🙏 ${holiday.message}`, 'warning');
            return;
          }

          // 2) Not a holiday — check booked slots
          const result = checkDateAvailability(value, formData.food_item_id);
          setIsDateAvailable(result.available);
          setAvailabilityMessage(result.message);

          if (!result.available) {
            setErrors(prev => ({ ...prev, meal_date: result.message }));
          } else {
            setErrors(prev => ({ ...prev, meal_date: '' }));
          }
        })();
      } else {
        setIsDateAvailable(true);
        setAvailabilityMessage('');
        setIsHoliday(false);
        setHolidayData(null);
        setErrors(prev => ({ ...prev, meal_date: '' }));
      }
    }
  };

  // Number to words function
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convert = (n) => {
      if (n < 20) return teens[n] || ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };
    return convert(Math.round(num)) + ' Rupees Only';
  };

  // Toast function
  const showToast = (message, type = 'info') => {
    const id = Date.now() + Math.random() * 1000;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const handleDonationTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      donation_type: type,
      purpose_id: '',
      food_item_id: '',
      meal_date: '',
      will_serve: '0',
      serving_members: '',
      arrival_time: '',
      special_notes: '',
      amount: ''
    }));
    setShowServingFields(false);
    setBookedSlots({});
    setIsDateAvailable(true);
    setAvailabilityMessage('');
    setSelectedMealName('');
    setIsHoliday(false);
    setHolidayData(null);
  };

  // ============================================
  // HANDLE PAYMENT SUCCESS - FIXED FOR DATABASE SCHEMA
  // ============================================
  const handlePaymentSuccess = async (response) => {
    try {
      setIsSubmitting(true);
      setPaymentError('');
      
      const verifyResult = await apiService.verifyRazorpayPayment({
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_order_id: response.razorpay_order_id,
        razorpay_signature: response.razorpay_signature
      });

      if (verifyResult.status) {
        const totalAmount = parseFloat(formData.amount);
        const donorNote = formData.message || '';

        // 1. Get donor ID from localStorage or donorData
        let donorId = localStorage.getItem('donorId');
        if (!donorId && donorData) {
          donorId = donorData.id;
        }

        // 2. Determine donation type - EXACT database values
        let donationType = '';
        let subType = '';
        let purposeText = '';
        let purposeId = null;

        if (formData.donation_type === 'sponsor_student') {
          donationType = 'student_sponsorship';
          subType = 'student_sponsorship';
          purposeText = 'Student Sponsorship';
          purposeId = null;
        } else if (formData.donation_type === 'sponsorship') {
          donationType = 'sponsor_donation';
          subType = 'sponsor';
          purposeText = purposes.find(p => p.id === parseInt(formData.purpose_id))?.purpose_name || 'Sponsorship Donation';
          purposeId = formData.purpose_id || null;
        } else if (formData.donation_type === 'food') {
          donationType = 'food_donation';
          subType = 'veg';
          const notesForPurpose = formData.special_notes || formData.message || '';
          purposeText = `Food Donation - ${selectedMealName || 'Meal'}${notesForPurpose ? ` - ${notesForPurpose}` : ''}`;
          purposeId = null;
        }

        // 3. Save Donation with CORRECT payment_status = 'completed'
        const donationData = {
          donor_id: donorId,
          donation_type: donationType,
          sub_type: subType,
          purpose: purposeText,
          purpose_id: purposeId,
          amount: totalAmount,
          currency: 'INR',
          payment_method: 'online',
          transaction_id: response.razorpay_payment_id,
          payment_status: 'completed',
          payment_date: new Date().toISOString().split('T')[0],
          is_anonymous: 0,
          tax_exempted: formData.pan_card ? 1 : 0,
          pan_card: formData.pan_card?.toUpperCase() || '',
          status: 'approved',
          notes: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            donation_type: formData.donation_type,
            donor_notes: donorNote,
            food_item_id: formData.food_item_id || null,
            meal_date: formData.meal_date || null,
            will_serve: formData.will_serve || '0',
            serving_members: formData.serving_members || null,
            arrival_time: formData.arrival_time || null,
          }),
          user_id: 0,
          delete_status: 0
        };

        console.log('Saving donation with data:', donationData);

        const donationResponse = await apiService.saveData('donations', donationData);
        
        if (!donationResponse.success) {
          throw new Error(donationResponse.message || 'Failed to save donation record');
        }

        const donationId = donationResponse.data?.id;
        const savedDonationCode = donationResponse.data?.donation_code || '';

        if (formData.donation_type === 'food' && formData.food_item_id && donationId) {
          const foodSponsorshipData = {
            donation_id: donationId,
            donor_id: donorId,
            food_id: formData.food_item_id,
            meal_type: 'veg',
            meal_date: formData.meal_date,
            will_serve: formData.will_serve === '1' ? 1 : 0,
            serving_members: parseInt(formData.serving_members) || 0,
            arrival_time: formData.arrival_time || null,
            total_amount: totalAmount,
            special_notes: formData.special_notes || formData.message || '',
            status: 'confirmed',
            user_id: 0,
            delete_status: 0
          };
          await apiService.saveData('food_sponsorships', foodSponsorshipData);
        }

        // 5. If student sponsorship, save to student_sponsorships table with donation_id
        if (formData.donation_type === 'sponsor_student' && donationId) {
          const studentSponsorshipData = {
            donation_id: donationId,
            donor_id: donorId,
            sponsorship_code: `SSP-${Date.now()}`,
            sponsorship_type: 'general',
            sponsorship_amount: totalAmount,
            start_date: new Date().toISOString().split('T')[0],
            status: 'active',
            user_id: 0,
            delete_status: 0
          };
          await apiService.saveData('student_sponsorships', studentSponsorshipData);
        }

        // 6. Save Receipt
        const receiptPayload = {
          donation_id: donationId,
          donor_id: donorId,
          receipt_type: formData.donation_type === 'sponsor_student' ? 'student_sponsor' :
                        formData.donation_type === 'sponsorship' ? 'sponsor' : 'food_donation',
          receipt_date: new Date().toISOString().split('T')[0],
          amount: totalAmount,
          currency: 'INR',
          pan_number: formData.pan_card?.toUpperCase() || '',
          phone: formData.phone || '',
          organization_name: formData.first_name + ' ' + formData.last_name || '',
          organization_address: formData.address || '',
          tax_exemption_certificate: formData.pan_card ? 'Yes' : 'No',
          additional_details: JSON.stringify({
            donation_code: savedDonationCode,
            donation_type: donationType,
            purpose: purposeText,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            donor_notes: donorNote,
          }),
          user_id: 0,
          delete_status: 0
        };

        const receiptResponse = await apiService.saveData('receipts', receiptPayload);
        const savedReceiptNumber = receiptResponse.data?.receipt_number || `RCP-${Date.now()}`;
        const receiptId = receiptResponse.data?.id;

        // Set receipt data for display
        const typeLabels = {
          'sponsor_student': 'Student Sponsorship',
          'sponsorship': 'Sponsorship Donation',
          'food': 'Food Donation'
        };

        setReceiptData({
          receiptNumber: savedReceiptNumber,
          date: new Date().toISOString().split('T')[0],
          donorName: formData.first_name + ' ' + formData.last_name || '',
          donorAddress: formData.address || '',
          donorPhone: formData.phone || '',
          donorPan: formData.pan_card || '',
          amount: totalAmount,
          amountInWords: numberToWords(totalAmount),
          paymentMode: 'Online (Razorpay)',
          purpose: typeLabels[formData.donation_type] || 'Donation',
          notes: donorNote,
          donationId: donationId,
          receiptId: receiptId,
          donationCode: savedDonationCode,
          paymentId: response.razorpay_payment_id,
        });

        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your generous donation!',
          receiptUrl: getReceiptUrl(receiptId),
          donationId: donationId,
          receiptId: receiptId,
          amount: totalAmount,
          receiptNumber: savedReceiptNumber,
          donorName: formData.first_name + ' ' + formData.last_name,
          paymentMethod: 'Online',
          purpose: typeLabels[formData.donation_type] || 'Donation'
        });

        showToast(`Donation successful! Receipt: ${savedReceiptNumber}`, 'success');

      } else {
        throw new Error(verifyResult.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentError(error.message || 'Payment verification failed. Please contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentFailure = (response) => {
    console.error('Payment failed:', response);
    setPaymentError('Payment failed. Please try again or use another payment method.');
    setIsSubmitting(false);
  };

  // ============================================
  // HANDLE FORM SUBMIT - FIXED
  // ============================================
  const handleSubmit = async (e) => { 
    e.preventDefault();
    setPaymentError('');
    if (formData.donation_type === 'food' && isHoliday) {
      setPaymentError(`🙏 ${holidayData?.message || 'Food donation is closed on this date. Kindly choose another date.'}`);
      return;
    }
    setIsSubmitting(true);

    try {
      if (!formData.phone || formData.phone.length < 10) {
        setPaymentError('Please enter a valid phone number');
        setIsSubmitting(false);
        return;
      }

      if (!formData.first_name || formData.first_name.trim().length < 2) {
        setPaymentError('Please enter your first name');
        setIsSubmitting(false);
        return;
      }

      if (formData.email && !formData.email.includes('@')) {
        setPaymentError('Please enter a valid email address');
        setIsSubmitting(false);
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 1) {
        setPaymentError('Please enter a valid amount (minimum ₹1)');
        setIsSubmitting(false);
        return;
      }

      // Check food availability before submitting
      if (formData.donation_type === 'food' && formData.meal_date && formData.food_item_id) {
        const result = checkDateAvailability(formData.meal_date, formData.food_item_id);
        if (!result.available) {
          setPaymentError(result.message);
          setIsSubmitting(false);
          return;
        }
      }

      if (!razorpayLoaded || typeof window.Razorpay === 'undefined') {
        setPaymentError('Payment system is loading. Please try again in a moment.');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'sponsorship' && !formData.purpose_id) {
        setPaymentError('Please select a purpose for sponsorship donation');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'food' && !formData.food_item_id) {
        setPaymentError('Please select a meal for food donation');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'food' && !formData.meal_date) {
        setPaymentError('Please select a meal date');
        setIsSubmitting(false);
        return;
      }

      // In handleSubmit - fix the donation_type mapping for Razorpay
      const getBackendDonationType = (type) => {
        const mapping = {
          'sponsor_student': 'sponsor',
          'sponsorship': 'sponsor',
          'food': 'food'
        };
        return mapping[type] || 'sponsor';
      };

      // Get proper purpose name
      const getPurposeName = () => {
        if (formData.donation_type === 'sponsor_student') {
          return 'Student Sponsorship';
        }
        if (formData.donation_type === 'sponsorship') {
          const purpose = purposes.find(p => p.id === parseInt(formData.purpose_id));
          return purpose?.purpose_name || 'Sponsorship Donation';
        }
        if (formData.donation_type === 'food') {
          const food = foodItems.find(f => String(f.id) === String(formData.food_item_id));
          return `Food Donation - ${food?.meal_name || 'Meal'}`;
        }
        return 'Donation';
      };

      const orderData = await apiService.createRazorpayOrder({
        amount: amount,
        donation_type: getBackendDonationType(formData.donation_type),
        donor_id: donorData?.id || '',
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        pan_card: formData.pan_card,
        address: formData.address,
        purpose: getPurposeName(),
        purpose_id: formData.purpose_id || '',
        meal_id: formData.food_item_id || '',
        meal_date: formData.meal_date || '',
        special_notes: formData.special_notes || '',
        will_serve: formData.will_serve || '0',
        serving_members: formData.serving_members || '',
        arrival_time: formData.arrival_time || '',
      });

      if (!orderData.status) {
        throw new Error(orderData.message || 'Failed to create payment order');
      }

      const typeLabels = {
        'sponsor_student': 'Sponsor Student',
        'sponsorship': 'Sponsorship Donation',
        'food': 'Food Donation'
      };

      const razorpayOptions = {
        key: 'rzp_live_TAV2geqqZfdp9D',
        amount: orderData.amount,
        currency: 'INR',
        order_id: orderData.order_id,
        name: 'Devnar Foundation',
        description: `Donation: ${typeLabels[formData.donation_type] || 'Donation'}`,
        image: '/images/devnar-logo.png',
        prefill: {
          name: formData.first_name + ' ' + formData.last_name,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          donation_type: formData.donation_type,
          donor_id: donorData?.id || '',
          first_name: formData.first_name,
          last_name: formData.last_name,
          pan_card: formData.pan_card,
          purpose_id: formData.purpose_id || '',
          meal_id: formData.food_item_id || '',
          meal_date: formData.meal_date || '',
        },
        theme: {
          color: '#E3A857'
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
            setPaymentError('Payment cancelled. Please try again.');
          }
        },
        handler: function(response) {
          handlePaymentSuccess(response);
        }
      };

      const razorpay = new window.Razorpay(razorpayOptions);
      
      razorpay.on('payment.failed', function(response) {
        handlePaymentFailure(response);
      });

      razorpay.open();

    } catch (error) {
      console.error('Payment initialization error:', error);
      setPaymentError(error.message || 'Failed to initialize payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormStatus({ submitted: false, success: false, message: '', receiptUrl: null });
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      pan_card: '',
      amount: '',
      donation_type: '',
      purpose_id: '',
      food_item_id: '',
      meal_date: '',
      will_serve: '0',
      serving_members: '',
      arrival_time: '',
      special_notes: '',
      message: '',
      paymentMethod: 'online'
    });
    setDonorData(null);
    setIsExistingDonor(false);
    setShowFullForm(false);
    setIsPhoneValid(false);
    setPaymentError('');
    setAmountError('');
    setShowServingFields(false);
    setBookedSlots({});
    setIsDateAvailable(true);
    setAvailabilityMessage('');
    setSelectedMealName('');
    setReceiptData(null);
    setIsHoliday(false);
    setHolidayData(null);
  };

  const getDonationTypeLabel = (type) => {
    const labels = {
      'sponsor_student': 'Sponsor Student',
      'sponsorship': 'Sponsorship Donation',
      'food': 'Food Donation'
    };
    return labels[type] || type;
  };

  // Check if bank details exist
  const hasBankDetails = () => {
    if (!websiteInfo) return false;
    return !!(websiteInfo.bank_account_name || websiteInfo.bank_account_number || websiteInfo.bank_name);
  };

  // --- Helper Components ---
  const DonationTypeSelector = () => (
    <div className="donation-type-selector">
      <label>Select Donation Type <span className="required-star">*</span></label>
      <div className="type-options">
        {['sponsor_student', 'sponsorship', 'food'].map((type) => (
          <button
            key={type}
            type="button"
            className={`type-option ${formData.donation_type === type ? 'active' : ''}`}
            onClick={() => handleDonationTypeSelect(type)}
          >
            <span className="type-icon">
              {type === 'sponsor_student' && '🎓'}
              {type === 'sponsorship' && '🤝'}
              {type === 'food' && '🍽️'}
            </span>
            <span className="type-label">{getDonationTypeLabel(type)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const PurposeSelector = () => (
    <div className="form-group">
      <label htmlFor="purpose_id"><i className="fas fa-bullseye"></i> Purpose <span className="required-star">*</span></label>
      <select
        id="purpose_id"
        name="purpose_id"
        value={formData.purpose_id}
        onChange={handleChange}
        required
      >
        <option value="">Select Purpose</option>
        {purposes.map(purpose => (
          <option key={purpose.id} value={purpose.id}>
            {purpose.purpose_name}
          </option>
        ))}
      </select>
    </div>
  );

const FoodDonationFields = () => {
  // Get the selected meal object - FIX: handle both string and number IDs
  const selectedFood = foodItems.find(item => String(item.id) === String(formData.food_item_id));

  // Debug - log the selected food
  console.log('Selected Food:', selectedFood);
  console.log('food_item_id:', formData.food_item_id);
  console.log('All food items:', foodItems);

  return (
    <>
      <div className="form-group">
        <label htmlFor="food_item_id"><i className="fas fa-utensils"></i> Select Meal <span className="required-star">*</span></label>
        <select
          id="food_item_id"
          name="food_item_id"
          value={formData.food_item_id}
          onChange={handleChange}
          required
        >
          <option value="">Select Meal</option>
          {foodItems.map(item => {
            const minQty = item.min_quantity || 1;
            const totalPrice = parseFloat(item.price_per_plate) * minQty;
            return (
              <option key={item.id} value={item.id}>
                {item.meal_name} ({item.meal_type}) - ₹{totalPrice.toFixed(0)} 
              </option>
            );
          })}
        </select>
      </div>

      {/* Meal Image Display when meal is selected */}
      {selectedFood && selectedFood.image ? (
        <div className="meal-image-container">
          <img 
            src={selectedFood.image.startsWith('http') 
              ? selectedFood.image 
              : `https://devnarfoundationfortheblind.org/admin/${selectedFood.image}`}
            alt={selectedFood.meal_name}
            className="meal-image"
            onError={(e) => {
              console.error('Image failed to load:', selectedFood.image);
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
            onLoad={() => console.log('Image loaded successfully')}
          />
          <div className="meal-image-overlay">
            <h4>{selectedFood.meal_name}</h4>
            <span className="meal-price">
              ₹{parseFloat(selectedFood.price_per_plate) * (selectedFood.min_quantity || 1)}
            </span>
          </div>
        </div>
      ) : formData.food_item_id ? (
        <div className="meal-image-placeholder">
          <p>No image available for this meal</p>
        </div>
      ) : null}

      <div className="form-group">
        <label htmlFor="meal_date"><i className="fas fa-calendar"></i> Meal Date <span className="required-star">*</span></label>
        <input
          type="date"
          id="meal_date"
          name="meal_date"
          value={formData.meal_date}
          onChange={handleChange}
          required
          min={new Date().toISOString().split('T')[0]}
          className={(!isDateAvailable || isHoliday) && formData.meal_date ? 'date-unavailable' : ''}
        />

        {/* ===== HOLIDAY NOTICE (Polite) ===== */}
        {formData.meal_date && isHoliday && holidayData && (
          <div className="holiday-notice" role="alert" aria-live="polite">
            <div className="holiday-notice-header">
              <div className="holiday-notice-icon" aria-hidden="true">🙏</div>
              <div className="holiday-notice-header-text">
                <div className="holiday-notice-title">We're taking a short break</div>
                <div className="holiday-notice-subtitle">
                  {holidayData.is_range ? 'Multi-day closure' : 'Single-day closure'}
                </div>
              </div>
            </div>

            <div className="holiday-notice-message">
              {holidayData.message || 'We are not accepting food donations on this date.'}
            </div>

            <div className="holiday-notice-range">
              <span className="holiday-notice-range-label">Closed from</span>
              <span className="holiday-notice-range-value">
                {holidayData.is_range
                  ? `${new Date(holidayData.from_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} → ${new Date(holidayData.to_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : new Date(holidayData.from_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>

            <div className="holiday-notice-tip">
              <span className="holiday-notice-tip-icon" aria-hidden="true">💡</span>
              <span>Kindly choose a different date to continue — we'd love to have you sponsor a meal then!</span>
            </div>
          </div>
        )}

        {/* Show error message if any (only when NOT a holiday) */}
        {errors.meal_date && !isHoliday && (
          <span className="error-text">{errors.meal_date}</span>
        )}

        {/* Show availability status (only when NOT a holiday) */}
        {formData.meal_date && formData.food_item_id && !errors.meal_date && !isHoliday && (
          <div className={`availability-status ${isDateAvailable ? 'available' : 'unavailable'}`}>
            {isDateAvailable ? (
              <span className="availability-icon">✅</span>
            ) : (
              <span className="availability-icon">❌</span>
            )}
            <span className="availability-message">
              {availabilityMessage || (isDateAvailable 
                ? '✅ Available - You can book this slot' 
                : '❌ This date is not available'
              )}
            </span>
          </div>
        )}

        {/* Show checking availability */}
        {(checkingAvailability || checkingHoliday) && (
          <div className="checking-availability">
            <span className="spinner-small"></span> Checking availability...
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="will_serve"><i className="fas fa-hand"></i> Will donor come to serve?</label>
        <select
          id="will_serve"
          name="will_serve"
          value={formData.will_serve}
          onChange={handleChange}
        >
          <option value="0">No</option>
          <option value="1">Yes</option>
        </select>
      </div>

      {showServingFields && (
        <>
          <div className="form-row serving-fields">
            <div className="form-group">
              <label htmlFor="serving_members"><i className="fas fa-users"></i> Number of people coming</label>
              <input
                type="number"
                id="serving_members"
                name="serving_members"
                value={formData.serving_members}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                min="1"
                placeholder="How many people?"
                className="serving-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="arrival_time"><i className="fas fa-clock"></i> Arrival Time</label>
              <input
                type="time"
                id="arrival_time"
                name="arrival_time"
                value={formData.arrival_time}
                onChange={handleChange}
                className="serving-input"
              />
            </div>
          </div>
        </>
      )}

      {/* Special Notes textarea */}
      <div className="form-group">
        <label htmlFor="special_notes"><i className="fas fa-comment"></i> Special Notes (Optional)</label>
        <textarea
          id="special_notes"
          name="special_notes"
          rows="2"
          value={formData.special_notes}
          onChange={handleChange}
          placeholder="Any special instructions or notes for the meal..."
          className="special-notes-textarea"
        />
        <small className="field-hint-text">
          <i className="fas fa-info-circle"></i> These notes will appear on your donation receipt
        </small>
      </div>
    </>
  );
};

  const DynamicFields = () => {
    switch (formData.donation_type) {
      case 'sponsor_student':
        return (
          <div className="form-group">
            <label><i className="fas fa-graduation-cap"></i> Student Sponsorship</label>
            <p className="field-hint">You are sponsoring a student's education. The amount will be used for tuition, books, and daily needs.</p>
          </div>
        );
      case 'sponsorship':
        return <PurposeSelector />;
      case 'food':
        return <FoodDonationFields />;
      default:
        return null;
    }
  };

  const cleanText = (text) => {
    if (!text) return '';
    const temp = document.createElement('div');
    temp.innerHTML = text;
    return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
  };
  
  // --- Bank Details Component ---
  const BankDetailsSection = () => {
    if (!hasBankDetails()) return null;

    const bankFields = [
      { key: 'bank_account_name', label: 'Account Holder Name', icon: 'fa-user' },
      { key: 'bank_account_number', label: 'Account Number', icon: 'fa-credit-card' },
      { key: 'bank_account_type', label: 'Account Type', icon: 'fa-layer-group' },
      { key: 'bank_name', label: 'Bank Name', icon: 'fa-university' },
      { key: 'bank_branch', label: 'Branch', icon: 'fa-code-branch' },
      { key: 'bank_address', label: 'Bank Address', icon: 'fa-map-pin' },
      { key: 'bank_ifsc_code', label: 'IFSC Code', icon: 'fa-qrcode' },
      { key: 'bank_rtgs_code', label: 'RTGS/NEFT Code', icon: 'fa-exchange-alt' }
    ];

    const hasAnyBankData = bankFields.some(field => websiteInfo && websiteInfo[field.key]);

    if (!hasAnyBankData) return null;

    return (
      <section className="bank-details-section">
        <div className="section-header">
          <span className="eyebrow">Payment Options</span>
          <h2>Bank Transfer Details</h2>
          <div className="divider">
            <span className="divider-line"></span>
            <span className="divider-diamond">◆</span>
            <span className="divider-line"></span>
          </div>
          <p className="section-subtitle">
            You can also make a direct bank transfer using the details below.
          </p>
        </div>

        <div className="bank-details-grid">
          {bankFields.map(field => {
            if (!websiteInfo || !websiteInfo[field.key]) return null;
            
            return (
              <div className="bank-detail-item" key={field.key}>
                <div className="bank-detail-icon">
                  <i className={`fas ${field.icon}`}></i>
                </div>
                <div className="bank-detail-content">
                  <span className="bank-detail-label">{field.label}</span>
                  <span className="bank-detail-value">{cleanText(websiteInfo[field.key])}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // --- IMPACT_STATS ---
  const IMPACT_STATS = [
    { value: '500+', label: 'Students Supported', icon: 'fa-users' },
    { value: '36+', label: 'Years of Service', icon: 'fa-calendar-alt' },
    { value: '100%', label: 'Free Education', icon: 'fa-book-open' },
    { value: '670+', label: 'Boarding Facilities', icon: 'fa-home' }
  ];

  // --- WAYS_TO_HELP ---
  const WAYS_TO_HELP = [
    {
      title: 'Sponsor Student',
      desc: 'Support a visually impaired student\'s education, including tuition, books, and daily needs.',
      href: '/donate?type=sponsor-student',
      icon: 'fa-graduation-cap'
    },
    {
      title: 'Sponsorship Donation',
      desc: 'Make a sponsorship donation to support our programs and infrastructure for the blind.',
      href: '/donate?type=sponsorship-donation',
      icon: 'fa-hand-holding-heart'
    },
    {
      title: 'Food Donation',
      desc: 'Provide nutritious meals to our students and help us maintain the mid-day meal program.',
      href: '/donate?type=food-donation',
      icon: 'fa-utensils'
    }
  ];

  return (
    <>
      <Header />
      <div className="donate-page devnar">
        <a className="skip-link" href="#main-donate">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-donate">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-donate">
            <span className="hero-badge">Make a Difference</span>
            <h1>Donate</h1>
            <p className="hero-subtitle">
              Support the education and empowerment of visually challenged children. 
              Your contribution helps us provide free education, boarding, medical care, 
              and vocational training to over 500 students.
            </p>
          </div>
        </section>

        <main id="main-donate" className="wrap page-content-donate">

          {/* --- Impact Stats --- */}
          <section className="impact-stats">
            <div className="impact-grid">
              {IMPACT_STATS.map((stat) => (
                <div key={stat.label} className="impact-stat">
                  <div className="impact-icon">
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <div className="impact-number">{stat.value}</div>
                  <div className="impact-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>



          {/* --- Donation Form --- */}
          <section id="donate-form" className="donation-form-section">
            <div className="section-title">
              <span className="eyebrow">Complete Your Donation</span>
              <h2>Donate Now</h2>
              <div className="rule" />
            </div>

            <div className="form-wrapper">
              {formStatus.submitted && formStatus.success ? (
                <div className="form-success">
                  <div className="success-header">
                    <span className="success-icon">✅</span>
                    <h3>Thank You for Your Generosity!</h3>
                    <p className="success-message">{formStatus.message}</p>
                  </div>

                  {formStatus.amount && (
                    <p className="success-amount">
                      <strong>Amount Donated: ₹{parseInt(formStatus.amount).toLocaleString()}</strong>
                    </p>
                  )}

                  <div className="success-actions">
                    {formStatus.receiptUrl && (
                      <a 
                        href={formStatus.receiptUrl} 
                        target="_blank" 
                        className="btn btn-primary btn-download-receipt"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-download"></i>
                        View & Download Receipt
                      </a>
                    )}
                    {!formStatus.receiptUrl && formStatus.success && (
                      <div className="receipt-notice">
                        <p><i className="fas fa-envelope"></i> Your receipt will be sent to your email and WhatsApp shortly.</p>
                      </div>
                    )}
                    <button onClick={handleReset} className="btn btn-outline">
                      <i className="fas fa-redo"></i> Make Another Donation
                    </button>
                    <a href="/" className="btn btn-outline">
                      <i className="fas fa-home"></i> Return Home
                    </a>
                  </div>
                </div>
              ) : (
                <form className="donate-form" onSubmit={handleSubmit}>
                  {/* Phone Number - First Field */}
                  <div className="form-row">
                    <div className="form-group phone-group">
                      <label htmlFor="phone">
                        <i className="fas fa-phone"></i> Phone Number <span className="required-star">*</span>
                      </label>
                      <div className="phone-input-wrapper">
                        <div className="phone-prefix">+91</div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="Enter 10-digit mobile number"
                          className={`phone-input ${isPhoneValid ? 'valid' : ''} ${formData.phone.length > 0 && !isPhoneValid ? 'error' : ''}`}
                          autoFocus
                          maxLength="10"
                        />
                        {isPhoneValid && (
                          <span className="phone-valid-icon">
                            <i className="fas fa-check-circle"></i>
                          </span>
                        )}
                      </div>
                      <small className="field-hint-text">
                        <i className="fas fa-info-circle"></i> Required for payment verification. Enter 10-digit mobile number.
                      </small>
                      {!isPhoneValid && formData.phone.length > 0 && formData.phone.length < 10 && (
                        <span className="error-text">
                          <i className="fas fa-exclamation-circle"></i> Please enter a valid 10-digit phone number
                        </span>
                      )}
                      {isExistingDonor && donorData && (
                        <span className="success-text">
                          <i className="fas fa-user-check"></i> Welcome back, {donorData.first_name}!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Show remaining fields only if phone is valid */}
                  {showFullForm && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="first_name"><i className="fas fa-user"></i> First Name <span className="required-star">*</span></label>
                          <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            required
                            placeholder="Your first name"
                            readOnly={isExistingDonor}
                            className={isExistingDonor ? 'readonly-field' : ''}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="last_name"><i className="fas fa-user"></i> Last Name</label>
                          <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            placeholder="Your last name"
                            readOnly={isExistingDonor}
                            className={isExistingDonor ? 'readonly-field' : ''}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email"><i className="fas fa-envelope"></i> Email Address</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            readOnly={isExistingDonor && formData.email}
                            className={isExistingDonor && formData.email ? 'readonly-field' : ''}
                          />
                          <small className="field-hint-text">
                            <i className="fas fa-info-circle"></i> Optional but recommended for receipt delivery
                          </small>
                        </div>
                        <div className="form-group">
                          <label htmlFor="pan_card"><i className="fas fa-id-card"></i> PAN Card</label>
                          <input
                            type="text"
                            id="pan_card"
                            name="pan_card"
                            value={formData.pan_card}
                            onChange={handleChange}
                            placeholder="ABCDE1234F"
                            maxLength="10"
                            readOnly={isExistingDonor && formData.pan_card}
                            className={isExistingDonor && formData.pan_card ? 'readonly-field' : ''}
                          />
                          <small className="field-hint-text">
                            <i className="fas fa-info-circle"></i> Required for 80G tax exemption certificate
                          </small>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="address"><i className="fas fa-map-marker-alt"></i> Address</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Your complete address"
                          readOnly={isExistingDonor && formData.address}
                          className={isExistingDonor && formData.address ? 'readonly-field' : ''}
                        />
                      </div>

                      {/* Donation Type Selector */}
                      <DonationTypeSelector />

                      {/* Dynamic Fields based on donation type */}
                      <DynamicFields />

                      <div className="form-row">
                        <div className="form-group amount-group">
                          <label htmlFor="amount">
                            <i className="fas fa-rupee-sign"></i> Amount (INR) <span className="required-star">*</span>
                          </label>
                          <div className="amount-input-wrapper">
                            <span className="currency-symbol">₹</span>
                            <input
                              type="number"
                              id="amount"
                              name="amount"
                              value={formData.amount}
                              onChange={handleChange}
                              min="1"
                              required
                              step="1"
                              placeholder="Enter amount"
                              className={`amount-input ${parseFloat(formData.amount) >= 1 ? 'valid' : ''} ${amountError ? 'error' : ''}`}
                            />
                          </div>
                          {amountError && (
                            <span className="error-text"><i className="fas fa-exclamation-circle"></i> {amountError}</span>
                          )}
                          {parseFloat(formData.amount) > 0 && !amountError && (
                            <span className="amount-hint">
                              <i className="fas fa-check-circle"></i> Amount: ₹{parseInt(formData.amount).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message"><i className="fas fa-comment"></i> Message (Optional)</label>
                        <textarea
                          id="message"
                          name="message"
                          rows="2"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Leave a message for our students..."
                        />
                      </div>

                      {/* Payment Error */}
                      {paymentError && (
                        <div className="payment-error">
                          <span><i className="fas fa-exclamation-triangle"></i></span>
                          <span>{paymentError}</span>
                          <button type="button" onClick={() => setPaymentError('')} className="error-close">×</button>
                        </div>
                      )}

                      <div className="form-actions">
                        <div className="form-note">
                          <p><i className="fas fa-dove"></i> Donations are eligible for tax exemption under section 80G.</p>
                          <p><i className="fas fa-lock"></i> Secure payment processing by Razorpay</p>
                        </div>
                        
                        <button 
                          type="submit" 
                          className="btn btn-primary submit-btn"
                          disabled={
                            isSubmitting || 
                            !isPhoneValid || 
                            parseFloat(formData.amount) < 1 || 
                            !formData.first_name || 
                            !!amountError || 
                            !formData.donation_type || 
                            (formData.donation_type === 'food' && !isDateAvailable) ||
                            (formData.donation_type === 'food' && isHoliday)
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-hand-holding-heart"></i> 
                              Support
                              <span className="btn-arrow"><i className="fas fa-arrow-right"></i></span>
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </section>

          {/* --- Bank Details Section --- */}
          <BankDetailsSection />

        </main>
      </div>
      <Footer />
    </>
  );
}